import * as React from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import type { TaskRecord } from "@nexus/contracts";
import {
  buildStatusMap,
  detectStatusTransitions,
} from "../lib/set-generation-state";
import { shouldSuppressSetGenerationToast } from "@/features/task-progress/lib/should-suppress-set-generation-toast";
import type { OpportunitySet } from "../types/opportunities.types";

/** How long the `justCompleted` pulse plays on a card after the READY transition. */
export const JUST_COMPLETED_MS = 4_000;

export interface UseGenerationTransitionEffectsOptions {
  /** Server-driven list. Transitions are detected between renders of this array. */
  sets: readonly OpportunitySet[];
  /** Active task-progress records — used to avoid duplicate completion toasts. */
  activeTasks?: readonly TaskRecord[];
  /** Called when the user clicks "View" on the completion toast. */
  onOpenSet: (setId: string) => void;
  /** Called when the user clicks "Retry" on a failed or empty-result toast. */
  onRetrySet?: (setId: string) => void;
  /** Called when the user clicks "Delete" on a failed or empty-result toast. */
  onDeleteSet?: (setId: string) => void;
}

export interface GenerationTransitionEffectsResult {
  /** Set of IDs that most recently flipped GENERATING → READY; clears after JUST_COMPLETED_MS. */
  justCompletedSetIds: Set<string>;
}

/**
 * Fires exactly one toast per GENERATING → READY / GENERATING → FAILED transition
 * observed across `sets` renders, and drives the short-lived `justCompleted` pulse.
 *
 * The route used to inline this logic with its own `useEffect` + `useRef` state;
 * pulled into a hook so toast/pulse wiring is unit-testable without mounting the
 * whole route or a QueryClientProvider.
 */
export function useGenerationTransitionEffects(
  { sets, activeTasks = [], onOpenSet, onRetrySet, onDeleteSet }: UseGenerationTransitionEffectsOptions,
): GenerationTransitionEffectsResult {
  const { t } = useTranslation();
  const [justCompletedSetIds, setJustCompletedSetIds] = React.useState<Set<string>>(() => new Set());
  const justCompletedTimers = React.useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const prevStatusRef = React.useRef<Map<string, OpportunitySet["status"]>>(new Map());
  /** Tracks toast IDs for persistent (duration=Infinity) toasts so they can be dismissed later. */
  const persistentToastIds = React.useRef<Map<string, string | number>>(new Map());

  // Stable refs so the effect below doesn't re-run when callbacks change identity.
  const onRetrySetRef = React.useRef(onRetrySet);
  const onDeleteSetRef = React.useRef(onDeleteSet);
  onRetrySetRef.current = onRetrySet;
  onDeleteSetRef.current = onDeleteSet;

  // Cleanup on unmount only — timers are keyed by setId and outlive individual renders.
  React.useEffect(() => {
    const timers = justCompletedTimers.current;
    return () => { timers.forEach((id) => clearTimeout(id)); };
  }, []);

  React.useEffect(() => {
    const transitions = detectStatusTransitions(prevStatusRef.current, sets);
    prevStatusRef.current = buildStatusMap(sets);

    // Dismiss persistent toasts for sets that are no longer in the list (deleted).
    const currentSetIds = new Set(sets.map((s) => s.id));
    for (const [setId, toastId] of persistentToastIds.current) {
      if (!currentSetIds.has(setId)) {
        toast.dismiss(toastId);
        persistentToastIds.current.delete(setId);
      }
    }

    for (const transition of transitions) {
      // Dismiss persistent toast when set is retried (FAILED → GENERATING).
      if (transition.from === "FAILED" && transition.to === "GENERATING") {
        const toastId = persistentToastIds.current.get(transition.setId);
        if (toastId !== undefined) {
          toast.dismiss(toastId);
          persistentToastIds.current.delete(transition.setId);
        }
      }

      if (transition.from === "GENERATING" && transition.to === "READY") {
        const suppressToast =
          !transition.totalCount || transition.totalCount > 0
            ? shouldSuppressSetGenerationToast(activeTasks, transition.setId)
            : false;
        const isEmpty = transition.totalCount === 0;

        if (isEmpty) {
          // Persistent toast — generation succeeded but found nothing.
          const toastId = toast(t("opportunities.toast.generationEmptyTitle", { name: transition.name }), {
              description: t("opportunities.toast.generationEmptyDescription"),
              duration: Infinity,
              ...(onRetrySetRef.current ? {
                action: {
                  label: t("common.retry", "Retry"),
                  onClick: () => onRetrySetRef.current?.(transition.setId),
                },
              } : {}),
              ...(onDeleteSetRef.current ? {
                cancel: {
                  label: t("opportunities.toast.deleteSet", "Delete set"),
                  onClick: () => onDeleteSetRef.current?.(transition.setId),
                },
              } : {}),
            });
            persistentToastIds.current.set(transition.setId, toastId);
        } else {
          // Timed toast — generation produced results.
          setJustCompletedSetIds((s) => {
            const n = new Set(s);
            n.add(transition.setId);
            return n;
          });
          if (!suppressToast) {
            toast(t("opportunities.toast.generationCompleteTitle"), {
              description: t("opportunities.toast.generationCompleteDescriptionNamed", { name: transition.name }),
              duration: 8_000,
              action: {
                label: t("opportunities.toast.viewSet"),
                onClick: () => onOpenSet(transition.setId),
              },
            });
          }
          const existing = justCompletedTimers.current.get(transition.setId);
          if (existing) clearTimeout(existing);
          justCompletedTimers.current.set(transition.setId, setTimeout(() => {
            justCompletedTimers.current.delete(transition.setId);
            setJustCompletedSetIds((s) => {
              const n = new Set(s);
              n.delete(transition.setId);
              return n;
            });
          }, JUST_COMPLETED_MS));
        }
      }

      if (transition.from === "GENERATING" && transition.to === "FAILED") {
        if (shouldSuppressSetGenerationToast(activeTasks, transition.setId)) {
          continue;
        }
        const toastId = toast.error(t("opportunities.toast.generationFailedTitle", { name: transition.name }), {
          description: t("opportunities.toast.generationFailedDescription"),
          duration: Infinity,
          ...(onRetrySetRef.current ? {
            action: {
              label: t("common.retry", "Retry"),
              onClick: () => onRetrySetRef.current?.(transition.setId),
            },
          } : {}),
          ...(onDeleteSetRef.current ? {
            cancel: {
              label: t("opportunities.toast.deleteSet", "Delete set"),
              onClick: () => onDeleteSetRef.current?.(transition.setId),
            },
          } : {}),
        });
        persistentToastIds.current.set(transition.setId, toastId);
      }
    }
  }, [sets, activeTasks, t, onOpenSet]);

  return { justCompletedSetIds };
}
